import { useEffect, useState } from "react";
import { useMpDungeonStore } from "../store/useMpDungeonStore";
import { useGameStore } from "../store/useGameStore";
import { CampaignMap } from "../components/CampaignMap";
import { BattleArena } from "../components/BattleArena";
import { ResultOverlay } from "../components/ResultOverlay";
import { PlayerProfileModal } from "../components/PlayerProfileModal";
import { leaderboardApi } from "../api/leaderboard";
import { useBattleReplay } from "../hooks/useBattleReplay";
import type { BattleLogEvent, PlayerProfile } from "../types";
import type { MpBattleRewards } from "../api/mpDungeons";

export function MpDungeonRunScreen() {
  const { hero, user, setScreen } = useGameStore();
  const { run, loading, error, poll, moveToNode, ackBattle, abandon } = useMpDungeonStore();
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    void poll();
    const t = setInterval(() => void poll(), 2500);
    return () => clearInterval(t);
  }, [poll]);

  const openProfile = async (userId: number) => {
    setProfileLoading(true);
    try {
      const profile = await leaderboardApi.getPlayerProfile(userId);
      setSelectedPlayer(profile);
    } catch {
      // non-fatal
    } finally {
      setProfileLoading(false);
    }
  };

  if (!run) {
    return (
      <div className="p-4 text-center text-gray-400 animate-pulse">
        Loading dungeon…
      </div>
    );
  }

  if (run.pending_battle?.log?.length) {
    return (
      <MpNodeBattle
        log={run.pending_battle.log}
        won={run.pending_battle.won}
        rewards={run.pending_battle.rewards ?? null}
        battleContextLabel={`MP Lv.${run.dungeon_level} (${run.difficulty})`}
        heroClass={hero?.hero_class}
        heroName={hero?.name ?? hero?.hero_class}
        fallbackMaxHp={hero?.stats.hp ?? 1000}
        viewerPlayerUid={user ? `player_${user.id}` : undefined}
        partyMembers={run.members.map((m) => ({
          user_id: m.user_id,
          name: m.name,
          hero_class: m.hero_class as import("../types").HeroClass,
        }))}
        onContinue={() => void ackBattle()}
      />
    );
  }

  const isDone = run.status === "completed" || run.status === "failed";
  const canMove = run.is_leader && !isDone;

  return (
    <div className="p-4 space-y-4 pb-8">
      <div className="text-center">
        <h2 className="text-xl font-bold font-game text-gold-400">
          🏰 MP Lv.{run.dungeon_level} ({run.difficulty})
        </h2>
        <p className="text-[11px] text-gray-500 mt-1">
          {run.is_leader ? "You are the leader — choose the next node" : "Follow the leader"}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 justify-center">
        {run.members.map((m) => (
          <button
            key={m.user_id}
            type="button"
            disabled={profileLoading}
            onClick={() => void openProfile(m.user_id)}
            className="text-[10px] px-2 py-1 rounded-full bg-kingdom-dark border border-kingdom-mid text-gray-300 hover:border-kingdom-glow disabled:opacity-60"
          >
            {m.name} Lv.{m.level}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
      )}

      {!isDone && (
        <CampaignMap
          nodes={run.nodes.map((n) => ({
            id: n.id,
            row: n.row,
            col: n.col,
            display_type: n.display_type,
            cleared: n.cleared,
            reachable: n.reachable,
          }))}
          edges={run.edges}
          isLoading={loading}
          onSelectNode={(id) => {
            if (canMove) void moveToNode(id);
          }}
        />
      )}

      {isDone && (
        <div className="text-center space-y-3 py-8">
          <div className="text-5xl">{run.status === "completed" ? "🏆" : "💀"}</div>
          <p className="text-lg font-bold text-white">
            {run.status === "completed" ? "Dungeon cleared!" : "Dungeon failed"}
          </p>
          <button
            type="button"
            onClick={() => setScreen("mpDungeons")}
            className="px-6 py-3 rounded-xl bg-kingdom-glow text-black font-bold"
          >
            Back to MP Dungeons
          </button>
        </div>
      )}

      {run.is_leader && !isDone && (
        <button
          type="button"
          disabled={loading}
          onClick={() => void abandon()}
          className="w-full py-2 text-sm text-rose-400 border border-rose-500/40 rounded-xl"
        >
          Abandon run
        </button>
      )}

      {!run.is_leader && (
        <button type="button" onClick={() => setScreen("main")} className="w-full py-2 text-sm text-gray-400">
          Leave screen (stay in party)
        </button>
      )}

      {selectedPlayer && (
        <PlayerProfileModal profile={selectedPlayer} onClose={() => setSelectedPlayer(null)} />
      )}
    </div>
  );
}

function MpNodeBattle({
  log,
  won,
  rewards,
  battleContextLabel,
  heroClass,
  heroName,
  fallbackMaxHp,
  partyMembers,
  viewerPlayerUid,
  onContinue,
}: {
  log: BattleLogEvent[];
  won: boolean;
  rewards: MpBattleRewards | null;
  battleContextLabel?: string;
  heroClass?: import("../types").HeroClass;
  heroName?: string;
  fallbackMaxHp: number;
  partyMembers?: Array<{
    user_id: number;
    name: string;
    hero_class: import("../types").HeroClass;
  }>;
  viewerPlayerUid?: string;
  onContinue: () => void;
}) {
  const { visibleLog, done } = useBattleReplay(log);

  return (
    <div className="relative h-full min-h-[70vh]">
      <BattleArena
        visibleLog={visibleLog}
        heroClass={heroClass}
        heroName={heroName}
        fallbackMaxHp={fallbackMaxHp}
        battleType="MP_DUNGEON"
        battleContextLabel={battleContextLabel}
        viewerPlayerUid={viewerPlayerUid}
        partyMembers={partyMembers?.map((m) => ({
          user_id: m.user_id,
          name: m.name,
          hero_class: m.hero_class,
        }))}
      />
      {done && (
        <ResultOverlay
          isWin={won}
          rewards={won ? rewards : null}
          buttonLabel="Continue"
          onContinue={onContinue}
        />
      )}
    </div>
  );
}
